import { useState } from "react";

import { toErrorMessage } from "../../../../components/Input/InstrumentSearch";
import { notify } from "../../../../components/Notification";
import { AdminInstrumentTodo } from "../../../../services/admin";
import {
  useRepairInstrumentListingMutation,
  useSetInstrumentSourceCountryMutation,
} from "../../../../services/instruments";
import {
  InstrumentFixSection,
  ManualPriceEntry,
  useManualPricingWrite,
  withFixSection,
} from "../../components/InstrumentEditor";
import { PendingFixItem } from "../types";
import { useWorkQueueSession } from "../WorkQueueProvider";

interface SavedFix {
  fix: PendingFixItem;
  section: InstrumentFixSection;
  instrument: AdminInstrumentTodo;
  isResolved: boolean;
  message: string;
}

export const useInstrumentFixActions = () => {
  const [setSourceCountry] = useSetInstrumentSourceCountryMutation();
  const [repairListing] = useRepairInstrumentListingMutation();
  const writeManualPricing = useManualPricingWrite();
  const { changeFilters, updateDraft, recordDecision, setFailure } =
    useWorkQueueSession();
  const [isFixing, setIsFixing] = useState(false);

  const whileFixing = async (action: () => Promise<void>) => {
    setFailure(null);
    setIsFixing(true);
    try {
      await action();
    } finally {
      setIsFixing(false);
    }
  };

  const failWith = (
    { key, symbol }: PendingFixItem,
    section: InstrumentFixSection,
    message: string,
  ) => setFailure({ key, symbol, section, message });

  const settle = ({
    fix,
    section,
    instrument,
    isResolved,
    message,
  }: SavedFix) => {
    if (isResolved) {
      recordDecision({
        key: fix.key,
        kind: "instrument",
        instrument,
        summary: message,
        resolvedAt: new Date().toISOString(),
      });
      return;
    }
    updateDraft("instrument", fix.key, (current) =>
      withFixSection(current, section, null),
    );
    // Its priority drops with each fixed problem, which must not move the admin to another row.
    changeFilters({ selectedKey: fix.key });
    notify.success(message);
  };

  const saveCountry = (fix: PendingFixItem, sourceCountry: string | null) =>
    whileFixing(async () => {
      try {
        await setSourceCountry({
          instrumentId: fix.instrument.id,
          sourceCountry,
        }).unwrap();
        settle({
          fix,
          section: "country",
          instrument: { ...fix.instrument, sourceCountry },
          isResolved: sourceCountry !== null && !fix.needsPricing,
          message: `${fix.symbol} source country ${sourceCountry ? "saved" : "cleared"}`,
        });
      } catch (err: unknown) {
        failWith(fix, "country", toErrorMessage(err));
      }
    });

  const repair = (fix: PendingFixItem, yahooSymbol: string) =>
    whileFixing(async () => {
      try {
        await repairListing({
          instrumentId: fix.instrument.id,
          yahooSymbol,
        }).unwrap();
        settle({
          fix,
          section: "listing",
          instrument: { ...fix.instrument, yahooSymbol },
          isResolved: !fix.needsCountry,
          message: `${fix.symbol} listing saved`,
        });
      } catch (err: unknown) {
        failWith(fix, "listing", toErrorMessage(err));
      }
    });

  const applyManualPricing = (fix: PendingFixItem, entry: ManualPriceEntry) =>
    whileFixing(async () => {
      const failure = await writeManualPricing(fix.instrument, entry);
      if (failure) {
        failWith(fix, "manual", failure);
        return;
      }
      settle({
        fix,
        section: "manual",
        instrument: {
          ...fix.instrument,
          yahooSymbol: null,
          manualPricingOnly: true,
        },
        isResolved: !fix.needsCountry,
        message: `${fix.symbol} now uses manual pricing`,
      });
    });

  return { isFixing, saveCountry, repair, applyManualPricing };
};
